import type { RouteLocationNormalizedGeneric } from '#vue-router'

import { useSidebarPrivileges } from '@entities/viewer'
import { useListsStore } from '@shared/model'
import { MAX_PER_PAGE, toPaginationValue } from '@shared/lib/routes'
import { foldBracketArrayParams } from '@shared/lib/url'

import { SURVEYS_ARRAY_QUERY_KEYS, getCanonicalSurveysQuery, queryToSurveysFilters } from '../lib/surveys-query'
import { useSurveysStore } from './store'

import { navigateTo } from '#app'

/**
 * Query → store filters for the surveys table, mirroring `middleware/orders.ts`. A query the filters cannot show
 * as-is (app-main's `status[0]=…` arrays, a stale survey type, a hidden tutor filter…) is rewritten once, so the
 * URL, the controls and the request always agree and a stale bookmark never gets a 422. `disciplineId` limits
 * the table to one discipline (its Survey tab); `null` is the general `/surveys` list.
 */
export const useMiddlewareSurveysQuery = async (
  to: RouteLocationNormalizedGeneric,
  disciplineId: number | null,
): Promise<Awaited<ReturnType<typeof navigateTo>> | undefined> => {
  const foldedQuery = foldBracketArrayParams(to.query, SURVEYS_ARRAY_QUERY_KEYS)
  const query = foldedQuery ?? to.query

  // The `lists` middleware only starts its request; the known types and sources are needed right now.
  const lists = await useListsStore().ensure()
  const { canViewTutors } = useSidebarPrivileges()

  const filters = queryToSurveysFilters(query, {
    lists: lists?.surveys ?? null,
    canFilterByTutor: canViewTutors.value,
  })
  const canonicalQuery = getCanonicalSurveysQuery(query, filters)

  if (foldedQuery || canonicalQuery) {
    return navigateTo({ path: to.path, query: canonicalQuery ?? query }, { replace: true })
  }

  const store = useSurveysStore()

  store.setDisciplineId(disciplineId)
  store.setFilters(filters)
  store.setCurrentPage(toPaginationValue(to.query?.page, 1))
  store.setPerPage(toPaginationValue(to.query?.per_page, store.getPerPage, MAX_PER_PAGE))
}
